import { Navigate, Outlet } from "react-router";
import { useUserProfileContext } from "@context/user_profile";
import { mayEnterOura, isOuraProd, isOuraEnabled } from "@oura/role";
import { OuraErrorBoundary } from "@oura/components/OuraErrorBoundary";
import { ClientJump } from "@oura/components/ClientJump";
import { WhatsNewBar } from "@oura/whatsnew/WhatsNewBar";
import { useOuraBrand, useOuraTabTitle } from "@oura/ui";

/**
 * Gate for the /oura/* product routes.
 *
 * Two independent checks, in order:
 *
 * 1. Is the product launched here at all? In production that means
 *    `VITE_ENABLE_OURA === "true"`; with the flag off nobody enters, whatever
 *    role they hold. This is checked FIRST and without waiting on the profile,
 *    so an unlaunched product cannot be probed by its loading behaviour.
 * 2. Is this session allowed in? Production is authoritative: an Oura role from
 *    the backend, or a platform admin — the same set the API itself authorizes
 *    (`AEO_STAFF_ROLES` in dependencies/aeo_auth.py, #3742). A guard stricter
 *    than the API it guards is how the menu ended up leading to /unauthorized
 *    on the day the product was switched on. In dev/staging the backend does
 *    not yet emit Oura roles, so any authenticated user may preview the product
 *    (the role is impersonated via the in-app dev switch).
 *
 * Authentication itself is already enforced by the enclosing ProtectedRoute.
 */
export default function OuraRoute() {
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    useOuraBrand();
    useOuraTabTitle();
    if (!isOuraEnabled()) return <Navigate to="/unauthorized" />;
    if (isLoadingUserProfile) return <></>;
    if (isOuraProd() && userInfo && !mayEnterOura(userInfo.role)) {
        return <Navigate to="/unauthorized" />;
    }
    // #3513: a render crash anywhere under /oura used to unmount the whole
    // document. Wrapping the outlet — rather than each screen — means every
    // present and future Oura route is covered by one boundary, including the
    // ones nobody has written yet.
    return (
        <OuraErrorBoundary>
            {/* Staff: one line on the latest release, across the top of every Oura page. */}
            <WhatsNewBar />
            <Outlet />
            {/* Staff: Ctrl/Cmd K jumps to any client from any Oura page. */}
            <ClientJump />
        </OuraErrorBoundary>
    );
}
